import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { API_URL } from '../config/api'

interface User {
  id: number
  email: string
  display_name: string
  role: string
  username?: string
  level?: number
  total_xp?: number
  followers_count?: number
  following_count?: number
  avatar?: string
  badges?: string
  wallet_balance?: number
  total_earnings?: number
  onboarding_status?: string
  work_role?: string
  package?: 'free' | 'pro' | 'enterprise'
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, displayName: string, role?: string) => Promise<void>
  logout: () => void
  reloadUser: () => Promise<void>
  isAuthenticated: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const storedToken = localStorage.getItem('token')
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(storedToken)
  // Token yoksa direkt false - race condition yok
  const [isLoading, setIsLoading] = useState(!!storedToken)

  const fetchCurrentUser = async (authToken: string) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
      if (res.status === 401) {
        // Token invalid - clear
        console.warn('Token invalid (401), clearing...')
        setUser(null)
        setToken(null)
        localStorage.removeItem('token')
      } else if (!res.ok) {
        // API error (500, etc) - keep token, just stop loading
        console.error('Failed to fetch user:', res.status, res.statusText)
        setIsLoading(false)
        return
      } else {
        const data = await res.json() as User
        setUser(data)
      }
    } catch (error) {
      console.error('Failed to fetch user:', error)
      // Network error - keep token, just stop loading
      setIsLoading(false)
      return
    } finally {
      setIsLoading(false)
    }
  }

  // Token varsa user bilgisini al (sadece mount'ta)
  useEffect(() => {
    const initAuth = async () => {
      const t = localStorage.getItem('token')
      if (t) {
        await fetchCurrentUser(t)
      } else {
        setIsLoading(false)
      }
    }
    void initAuth()
  }, [])

  const reloadUser = async () => {
    const t = localStorage.getItem('token')
    if (t) {
      await fetchCurrentUser(t)
    }
  }

  const login = async (email: string, password: string) => {
    console.log('🔐 Login attempt:', email)

    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Login failed' })) as { detail?: string }
        throw new Error(err.detail || 'Login failed')
      }

      const data = await res.json() as { access_token?: string; token?: string; user: User }

      const newToken = data.access_token ?? data.token ?? ''
      const newUser = data.user

      console.log('💾 Setting token:', newToken?.substring(0, 20) + '...')
      console.log('👤 Setting user:', newUser)

      localStorage.setItem('token', newToken)
      setToken(newToken)
      setUser(newUser)

      console.log('✨ Login complete, isAuthenticated:', !!newUser)
    } catch (error) {
      console.error('❌ Login error in AuthContext:', error)
      throw error
    }
  }

  const register = async (email: string, password: string, displayName: string, role: string = 'both') => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, display_name: displayName, role })
    })

    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Registration failed' })) as { detail?: string }
      throw new Error(err.detail || 'Registration failed')
    }

    const data = await res.json() as { access_token?: string; token?: string; user?: User }
    const newToken = data.access_token ?? data.token ?? ''
    localStorage.setItem('token', newToken)
    setToken(newToken)
    if (data.user) {setUser(data.user)}
  }

  const logout = () => {
    setToken(null)
    setUser(null)
    localStorage.removeItem('token')
    localStorage.removeItem('session_token')
    localStorage.removeItem('user')
    window.location.href = '/login'
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        logout,
        reloadUser,
        isAuthenticated: !!user,
        isLoading
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
